import React, { Component } from "react";
import { AppCard, StatusBadge, DataTableToolbar, PageLayout } from "../../components/ui";

import { Merchant, MerchantStatus } from "../../models/MerchantModel";
import history from "../../history";
import { toastService } from "../../services/toastService";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { merchantApiService } from "../../services/api/merchants/MerchantApiService";
import { utils } from "../../utils/utils";
import _ from "lodash";
import { fileService } from "../../services/FileService";
import { SearchRequestBaseModel, SortOrderType } from "../../models/SearchRequestModel";
import ExtendedDataTable from "../../components/ExtendedDataTable";
import { Column } from "primereact/column";
import { stringFilterMatchModeOptions, numberFilterMatchModeOptions, dateFilterTemplate } from "../Paginator";
import { getUserPermittedRoutes } from "../../authGuard";
import { Button } from "primereact/button";
import { Building2, Plus, Users } from "lucide-react";

interface UsersState {
    merchants: Merchant[];
    globalFilterValue: string;
    isLoading: boolean;
    totalRecords: number;
}

export default class Merchants extends Component<Record<string, never>, UsersState> {
    private dataTable: ExtendedDataTable | null = null;

    constructor(props: Record<string, never>) {
        super(props);

        this.state = {
            merchants: [],
            globalFilterValue: "",
            isLoading: false,
            totalRecords: 0,
        };
    }

    loadMerchantsData = async (searchRequest: SearchRequestBaseModel) => {
        this.setState({ isLoading: true });
        searchRequest.filters = utils.manageDataTableFilters(searchRequest.filters);
        const merchantsResult = await merchantApiService.getAll(searchRequest);
        if (!merchantsResult.success) {
            toastService.error(merchantsResult.message);
        }
        const merchants: Merchant[] = merchantsResult.data ?? [];
        // logo holds the file id until its image is fetched; show placeholders meanwhile
        const logoIds = merchants.map((m) => ({ id: m.id, logo: m.logo }));
        merchants.forEach((m) => (m.logo = ""));
        this.setState({ merchants, totalRecords: merchantsResult.totalCount, isLoading: false });
        this.loadMerchantImages(logoIds);
    };

    updateMerchantData = (merchantId: number, updatedData: Record<string, string>) => {
        this.setState((prevState) => {
            const merchantIndex = prevState.merchants.findIndex((f) => f.id == merchantId);

            if (merchantIndex === -1) {
                return null;
            }

            const updatedList = [
                ...prevState.merchants.slice(0, merchantIndex),
                Object.assign({}, prevState.merchants[merchantIndex], updatedData),
                ...prevState.merchants.slice(merchantIndex + 1),
            ];

            return { merchants: updatedList };
        });
    };

    loadMerchantImages = (logos: { id: number; logo: string }[]) => {
        logos.forEach(async ({ id, logo }) => {
            // logo was never assigned. Ignore
            if (!logo || logo.length === 0) {
                return;
            }

            const logoResult = await fileService.GetFile(logo);
            if (!logoResult.success) {
                return;
            }

            this.updateMerchantData(id, { logo: utils.appendBase64Data(logoResult.data) });
        });
    };

    onRowClick = (event: any) => {
        const merchant = event.data as Merchant;
        history.push(`/merchant/${merchant.id}`);
    };

    renderMerchantLogo(rowData: Merchant) {
        return (
            <div>
                {rowData.logo && rowData.logo.length > 0 ? (
                    <img
                        alt=""
                        src={rowData.logo}
                        style={{ maxHeight: "100%", maxWidth: "100px" }}
                        onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = "none";
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) placeholder.style.display = "flex";
                        }}
                    />
                ) : null}
                <div
                    className="w-[60px] h-[60px] rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center"
                    style={{ display: rowData.logo && rowData.logo.length > 0 ? "none" : "flex" }}
                >
                    <Building2 size={28} strokeWidth={1.25} className="text-neutral-300 dark:text-neutral-600" />
                </div>
            </div>
        );
    }

    onGlobalFilterChange = (e: { target: { value: any } }) => {
        const value = e.target.value;

        this.setState({ globalFilterValue: value });
    };

    renderHeader = (hasActiveFilters: boolean) => {
        return (
            <DataTableToolbar
                onClear={() => this.dataTable!.reset()}
                hasActiveFilters={hasActiveFilters}
            />
        );
    };

    renderPageActions = () => {
        const path = "/merchant/create";
        const canCreate = getUserPermittedRoutes().some((s) => s.path == path);
        if (!canCreate) return null;
        return (
            <Button
                label="Add Merchant"
                icon={<Plus size={16} />}
                onClick={() => history.push(path)}
            />
        );
    };

    render() {
        return (
            <PageLayout heading="Merchants" icon={Users} className="!px-0" actions={this.renderPageActions()}>
                <AppCard className="mb-3">
                    <AppCard.Body className="!p-0">
                        <ExtendedDataTable
                            header={this.renderHeader}
                            ref={(ref) => (this.dataTable = ref)}
                            value={this.state.merchants}
                            onChange={this.loadMerchantsData}
                            loading={this.state.isLoading}
                            totalRecords={this.state.totalRecords}
                            defaultSortField="createdTimeStamp"
                            defaultSortOrder={SortOrderType.Descending}
                            onRowClick={this.onRowClick}
                        >
                            <Column {...({ alwaysVisible: true } as any)} field="id" header="Id" filterMatchModeOptions={numberFilterMatchModeOptions}></Column>
                            <Column  field="logo" header="Logo" body={this.renderMerchantLogo}></Column>
                            <Column {...({ alwaysVisible: true } as any)} field="name" header="Name" filterMatchModeOptions={stringFilterMatchModeOptions}></Column>
                            <Column
                                field="status"
                                header="KYC Status"
                                {...({ alwaysVisible: true } as any)}
                                body={(rowData: Merchant) =>
                                    StatusBadge(
                                        rowData.status == MerchantStatus.Active,
                                        "Active",
                                        "Inactive",
                                    )
                                }
                                filterMatchModeOptions={stringFilterMatchModeOptions}
                            ></Column>
                            <Column
                                field="createdTimeStamp"
                                header="Registration Date"
                                body={dateBodyTemplate("createdTimeStamp")}
                                filterMatchModeOptions={numberFilterMatchModeOptions}
                                dataType="date"
                                filterElement={dateFilterTemplate}
                            ></Column>
                        </ExtendedDataTable>
                    </AppCard.Body>
                </AppCard>
            </PageLayout>
        );
    }
}
